// =====================================================
// SOCIETIES API CLIENT (Con currentSiteId para indicador visual)
// =====================================================

import { api } from './client';
import type {
  Society,
  SocietySelect,
  CreateSocietyRequest,
  UpdateSocietyRequest,
  SocietyLinkedSite,
  UpdateSiteLinksRequest,
} from '../types/catalog-admin';
import type { PaginationQuery, PaginatedResponse } from '../types/pagination';

const BASE_URL = '/societies';

export interface SocietiesPaginationQuery extends PaginationQuery {
  siteId?: string;        // Para FILTRAR solo sociedades de ese centro
  currentSiteId?: string; // Para OBTENER info de isDefaultInCurrentSite
}

export const societiesApi = {
  /**
   * Listar todas (sin paginación - mantener compatibilidad)
   */
  getAll: async (includeInactive = false): Promise<Society[]> => {
    const query = includeInactive ? '?includeInactive=true' : '';
    return api.get<Society[]>(`${BASE_URL}${query}`);
  },

  /**
   * Listar con paginación (con filtro opcional por centro)
   */
  getAllPaginated: async (params: SocietiesPaginationQuery = {}): Promise<PaginatedResponse<Society>> => {
    const searchParams = new URLSearchParams();
    
    if (params.page) searchParams.set('page', params.page.toString());
    if (params.limit) searchParams.set('limit', params.limit.toString());
    if (params.search) searchParams.set('search', params.search);
    if (params.sortBy) searchParams.set('sortBy', params.sortBy);
    if (params.sortOrder) searchParams.set('sortOrder', params.sortOrder);
    if (params.includeInactive) searchParams.set('includeInactive', 'true');
    if (params.siteId) searchParams.set('siteId', params.siteId);
    if (params.currentSiteId) searchParams.set('currentSiteId', params.currentSiteId);

    const queryString = searchParams.toString();
    const url = `${BASE_URL}/paginated${queryString ? `?${queryString}` : ''}`;
    
    return api.get<PaginatedResponse<Society>>(url);
  },

  /**
   * Obtener lista para select (solo activas)
   */
  getForSelect: async (): Promise<SocietySelect[]> => {
    return api.get<SocietySelect[]>(`${BASE_URL}/select`);
  },

  /**
   * Obtener por ID
   */
  getById: async (id: string): Promise<Society> => {
    return api.get<Society>(`${BASE_URL}/${id}`);
  },

  /**
   * Obtener centros vinculados a una sociedad
   */
  getLinkedSites: async (id: string): Promise<SocietyLinkedSite[]> => {
    return api.get<SocietyLinkedSite[]>(`${BASE_URL}/${id}/sites`);
  },

  /**
   * Actualizar vinculaciones de centros (batch)
   */
  updateSiteLinks: async (
    id: string,
    links: UpdateSiteLinksRequest,
  ): Promise<{ success: boolean; message: string }> => {
    return api.patch<{ success: boolean; message: string }>(
      `${BASE_URL}/${id}/sites`,
      links,
    );
  },

  /**
   * Crear
   */
  create: async (data: CreateSocietyRequest): Promise<Society> => {
    return api.post<Society>(BASE_URL, data);
  },

  /**
   * Actualizar
   */
  update: async (id: string, data: UpdateSocietyRequest): Promise<Society> => {
    return api.patch<Society>(`${BASE_URL}/${id}`, data);
  },

  /**
   * Desactivar (soft delete)
   */
  deactivate: async (id: string): Promise<Society> => {
    return api.delete<Society>(`${BASE_URL}/${id}`);
  },

  /**
   * Reactivar
   */
  reactivate: async (id: string): Promise<Society> => {
    return api.patch<Society>(`${BASE_URL}/${id}/reactivate`);
  },
};

export default societiesApi;
